import { useEffect, useMemo, useState } from 'react'
import { useTypedSelector } from '../../../hooks/useTypedSelector'
import { ECardType, ICard } from '../../../types/card'
import { TokenHistoryView } from '../../../types/lot'
import { lot as lotAPI } from '../../../utils/api/index'
import { Activities } from '../../Activities'
import { NFTList } from '../NFT'
import { createRef } from 'react'
import { PlaceABet } from '../../Bet'
import { useContext } from 'react'
import { EventContext } from '../../../ctx/event'
import { Button } from '../Buttons'
import { CardFile } from './File'
import { CardIconButtons } from './IconButtons'
import { errors, notifications } from '../../../utils/notification'
import { useTimer } from '../../../hooks/useTimer'
import { buyALot } from '../../../utils/signatures/buy'
import { useCardMeta } from '../../../hooks/useCardMeta'
import { Price } from '../Price'
import { UserMini } from '../../Users'
import { Margin } from '../../../hoc/Margin'
import { useCardContent } from '../../../hooks/useCardContent'
import { useDispatch } from 'react-redux'
import { action_setLoading } from '../../../store/actions/popup'

interface IPropsCardDetail {
    card: ICard
    activities?: TokenHistoryView[]
}

export const CardDetail = ({ card, activities }: IPropsCardDetail) => {
    const { auth } = useTypedSelector((store) => store)
    const dispatch = useDispatch()

    const [lastBetAmount] = useState(card.currentCost)
    const [showPlaceABid, setShowPlaceABid] = useState(false)
    const [fullscreen, setFullscreen] = useState(false)
    let fullscreenRef = createRef<HTMLVideoElement & HTMLImageElement>()
    const { onCardUpdated, onBet, onLotBuy } = useContext(EventContext)

    const { isAuthor, isAuction, inSales, canBetCancel, isOriginalToken } =
        useCardMeta(card)
    const content = useCardContent(card)

    useEffect(() => {
        document.addEventListener('fullscreenchange', handlerFullscreenChande)
        return () => {
            document.removeEventListener(
                'fullscreenchange',
                handlerFullscreenChande
            )
        }
    }, [])

    const timer = useTimer(card.expired, card)

    const showFullscreenBtn = useMemo(
        () => document.fullscreenEnabled && !!content,
        [content]
    )

    function toggleFS() {
        if (!document.fullscreenEnabled) {
            errors.fullscreen()
            return
        }
        if (!document.fullscreenElement) {
            fullscreenRef.current?.requestFullscreen()
            return
        }
        document.exitFullscreen()
    }
    function handlerFullscreenChande() {
        setFullscreen(!!document.fullscreenElement)
    }

    async function finish() {
        await lotAPI.finishAuction({ lotId: card.id })
        if (onCardUpdated) onCardUpdated()
    }

    async function cancelBet() {
        if (!card.LastBet?.id) {
            return
        }
        await lotAPI.cancelABet({ lotBetId: card.LastBet.id })
        if (onBet) onBet()
    }

    async function buyToken() {
        const token = card.LotTokens?.find((t) => !t.isSold)
        if (!token || !token.TokenNFT) {
            return
        }
        setShowPlaceABid(true)
        dispatch(action_setLoading(true))
        try {
            await buyALot(card.id, card.currentCost || '0', token.TokenNFT)
            notifications.bought()
            if (onLotBuy) onLotBuy()
        } catch (e) {
            console.error(e)
        }
        setShowPlaceABid(false)
        dispatch(action_setLoading(false))
    }

    return (
        <>
            <section className="mainframe" ref={fullscreenRef}>
                <div
                    className={`mainframe__file ${
                        fullscreen ? 'mainframe__file-fullscreen' : ''
                    } ${!showFullscreenBtn ? 'mainframe__file-opened' : ''}`}
                >
                    <CardFile card={card}></CardFile>
                    {showFullscreenBtn ? (
                        <div className="mainframe__fullbtn" onClick={toggleFS}>
                            {fullscreen ? (
                                <i className="fas fa-compress-arrows-alt"></i>
                            ) : (
                                <i className="fas fa-expand-arrows-alt"></i>
                            )}
                        </div>
                    ) : null}
                </div>
                <div className="mainframe__block">
                    <div className="mainframe__head">
                        <div className="mainframe__user">
                            {card.User ? (
                                <UserMini user={card.User}></UserMini>
                            ) : null}
                        </div>
                        <div className="mainframe__actions">
                            <CardIconButtons card={card}></CardIconButtons>
                        </div>
                    </div>
                    <div className="mainframe__main">
                        <div className="mainframe__title">{card.name}</div>
                        {!isOriginalToken &&
                            card.LotTokens &&
                            (card.LotTokens.length > 1 ? (
                                <Margin margin={{ left: 10 }}>
                                    <div className="mainframe__info">
                                        <span>Total editions: </span>
                                        {card.LotTokens.length}
                                    </div>
                                </Margin>
                            ) : (
                                <Margin margin={{ left: 10 }}>
                                    <div className="mainframe__info">
                                        <span>Copy: </span>
                                        {(card.TokensNFT?.find(() => true)
                                            ?.index || 0) + 1}{' '}
                                        of {card.copiesTotal}
                                    </div>
                                </Margin>
                            ))}
                    </div>
                    <div className="mainframe__info">
                        <span>Description: </span>
                        {card.description}
                    </div>
                    <div className="mainframe__info">
                        <span>Author’s reward: </span>
                        {card.creatorReward}%
                    </div>
                    {card.isCommercial && (
                        <div className="mainframe__info">
                            <i>This NFT comes with commercial rights</i>
                        </div>
                    )}

                    {inSales ? (
                        <div className="mainframe__bid bid">
                            {lastBetAmount ? (
                                <div className="bid__item">
                                    <div className="bid__item-title">
                                        Min. bid required:
                                    </div>
                                    <Price wei={lastBetAmount}></Price>
                                </div>
                            ) : null}
                            {timer && isAuction ? (
                                <div className="bid__item">
                                    <div className="bid__item-title">
                                        Auction ends:
                                    </div>
                                    <div className="bid__item-content">
                                        <p>{timer}</p>
                                    </div>
                                </div>
                            ) : null}
                            {!!auth.id ? (
                                isAuction ? (
                                    isAuthor ? (
                                        <div className="bid__item">
                                            <Button
                                                onClick={finish}
                                                fill
                                                stylePreset="semitransparent"
                                                size="small"
                                            >
                                                Finish Auction
                                            </Button>
                                        </div>
                                    ) : canBetCancel ? (
                                        <div className="bid__item">
                                            <Button
                                                onClick={cancelBet}
                                                fill
                                                stylePreset="semitransparent"
                                                size="small"
                                            >
                                                Cancel a bid
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="bid__item">
                                            <Button
                                                onClick={() =>
                                                    setShowPlaceABid(true)
                                                }
                                                fill
                                                stylePreset="semitransparent"
                                                size="small"
                                            >
                                                Place a bid
                                            </Button>
                                        </div>
                                    )
                                ) : isAuthor ? (
                                    <div className="bid__item">
                                        <Button
                                            onClick={finish}
                                            fill
                                            stylePreset="semitransparent"
                                            size="small"
                                        >
                                            Cancel the sale
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="bid__item">
                                        <Button
                                            onClick={buyToken}
                                            fill
                                            stylePreset="semitransparent"
                                            size="small"
                                        >
                                            Buy
                                        </Button>
                                    </div>
                                )
                            ) : null}
                        </div>
                    ) : null}
                    {card.cardType === ECardType.lot ? (
                        <Activities activities={activities}></Activities>
                    ) : card.TokensNFT?.length ? (
                        <NFTList
                            nftTokens={card.TokensNFT}
                            tokenOriginalId={card.id}
                        ></NFTList>
                    ) : null}
                </div>
            </section>
            <PlaceABet
                card={card}
                show={[showPlaceABid, setShowPlaceABid]}
            ></PlaceABet>
        </>
    )
}
